import React, { useCallback, useEffect, useMemo, useState } from 'react'
import FbButton, { FbButtonVariants } from 'components/FbUI/FbButton'
import { BsCheck, BsChevronDown } from 'react-icons/bs'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../command'
import { cn } from '../cn'

export interface ISelectItem<T = any> {
  label: string
  icon?: React.JSX.Element
  value: T
}

export interface IMultiSelectProps<T> {
  items?: ISelectItem<T>[]
  selected: T[]
  onSelect: (items: T[]) => void
  error?: string
  buttonLabel?: string | React.JSX.Element
  buttonLabelClassName?: string
  buttonIcon?: React.JSX.Element
  buttonVariant?: FbButtonVariants
  menuLabel?: string
  disabled?: boolean
  className?: string
  buttonClassName?: string
  disableChevronIcon?: boolean
  enableSelectAll?: boolean
  selectAllLabel?: string
  selectAllBehavior?: 'toggle' | 'selectNone'
  searchPlaceholder?: string
  notFoundPlaceholder?: string
}

export function MultiSelect<T>(props: IMultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [tempSelected, setTempSelected] = useState<T[]>(props.selected)

  const allValues = useMemo(
    () => props.items?.map((item) => item.value) ?? [],
    [props.items]
  )

  const isAllSelected = useMemo(() => {
    if (props.selectAllBehavior === 'selectNone') {
      return tempSelected.length === 0
    } else {
      return (
        allValues.length > 0 &&
        allValues.every((value) => tempSelected.includes(value))
      )
    }
  }, [allValues, tempSelected])

  useEffect(() => {
    setTempSelected(props.selected)
  }, [props.selected])

  const handleSelect = useCallback(
    (item: ISelectItem<T>) => {
      const newSelection = tempSelected.includes(item.value)
        ? tempSelected.filter((i) => i !== item.value)
        : [...tempSelected, item.value]

      setTempSelected(newSelection)
      props.onSelect(newSelection)
    },
    [tempSelected, props.onSelect]
  )

  const handleSelectAll = useCallback(() => {
    let newSelection: T[]

    if (props.selectAllBehavior === 'selectNone') {
      newSelection = []
    } else {
      // Default 'toggle' behavior
      newSelection = isAllSelected ? [] : allValues
    }

    setTempSelected(newSelection)
    props.onSelect(newSelection)
  }, [props.selectAllBehavior, isAllSelected, allValues, props.onSelect])

  const getDisplayText = () => {
    if (props.enableSelectAll && isAllSelected) {
      return 'All'
    } else if (tempSelected.length > 0) {
      return `${tempSelected.length} selected`
    } else {
      return <span className="text-gray-500">Select items</span>
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger disabled={props.disabled} asChild>
        <FbButton
          className={cn('flex-1 border-[#DEE2E6]', props.buttonClassName)}
          variant={props.buttonVariant}
        >
          <div className="flex items-center justify-between gap-2 flex-1 min-w-0">
            <div className="flex items-center justify-start w-[90%] max-w-[90%]">
              {props.buttonIcon}
              {props.buttonLabel && (
                <b className={cn('truncate', props.buttonLabelClassName)}>
                  {props.buttonLabel}:&nbsp;
                </b>
              )}
              {getDisplayText()}
            </div>
            {!props.disableChevronIcon && (
              <div>
                <BsChevronDown />
              </div>
            )}
          </div>
        </FbButton>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'w-[400px] p-0',
          {
            'opacity-50': props.disabled,
            'border-red-400': props.error,
          },
          props.className
        )}
      >
        <Command>
          <CommandInput
            placeholder={props.searchPlaceholder ?? 'Search items...'}
          />
          <CommandList>
            <CommandEmpty>
              {props.notFoundPlaceholder ?? 'No items found.'}
            </CommandEmpty>
            <CommandGroup>
              {props.enableSelectAll && (
                <CommandItem
                  key={'select-all'}
                  value={'select-all'}
                  onSelect={handleSelectAll}
                >
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="flex items-center gap-2">
                      {props.selectAllLabel || 'All'}
                    </div>
                    <div>{isAllSelected && <BsCheck />}</div>
                  </div>
                </CommandItem>
              )}
              {props.items?.map((item) => (
                <CommandItem
                  key={String(item.value)}
                  value={item.label + String(item.value)}
                  onSelect={() => handleSelect(item)}
                >
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="flex items-center gap-2">
                      {item.icon}
                      {item.label}
                    </div>
                    <div>
                      {tempSelected.includes(item.value) && <BsCheck />}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
